export type EntityTableBuiltInRenderer =
	| 'boolean'
	| 'dateTime'
	| 'list'
	| 'color'
	| 'number'
	| 'enum'
	| 'object'

export type EntityTableEnumDisplay = 'text' | 'iconWithTranslatedLabel'

export type EntityTableEnumLike = Record<string, string | number>

export type EntityTableColumnFilterConfig =
	| { type: 'enum'; enumKey: string; enumObject: EntityTableEnumLike }
	| { type: 'builtIn'; renderer: EntityTableBuiltInRenderer }

export type EntityTableColumnConfig =
	| string
	| {
			key: string
			renderer?: EntityTableBuiltInRenderer
			enumKey?: string
			enumObject?: EntityTableEnumLike
			enumDisplay?: EntityTableEnumDisplay
			filter?: EntityTableColumnFilterConfig
			relationCount?: { relationKey: string }
			detailLink?: boolean
			objectKey?: string
	  }

export type EntityTableAction<TRow = unknown> = {
	type: 'link' | 'button'
	id: string
	label: string
	icon?: string
	style?: 'link' | 'button' | 'iconButton' | 'pillButton'
	href?: (row: TRow) => string
	onClick?: (row: TRow) => void
}

export type EntityTableConfig<TRow = unknown> = {
	columns: readonly EntityTableColumnConfig[]
	allowedColumnKeys?: readonly string[]
	actions?: readonly EntityTableAction<TRow>[]
	defaultColumnOrder?: readonly string[]
	defaultColumnVisibility?: Record<string, boolean>
}

export function cellRenderer(
	key: string,
	renderer: EntityTableBuiltInRenderer,
): EntityTableColumnConfig {
	return {
		key,
		renderer,
		filter: { type: 'builtIn', renderer },
	}
}

export function relationCountRenderer(
	key: string,
	relationKey: string,
): EntityTableColumnConfig {
	return {
		key,
		renderer: 'number',
		filter: { type: 'builtIn', renderer: 'number' },
		relationCount: { relationKey },
	}
}

export function enumRenderer<TEnum extends EntityTableEnumLike>(
	key: string,
	enumObject: TEnum,
	enumKey: string,
): EntityTableColumnConfig {
	return {
		key,
		renderer: 'enum',
		enumObject,
		enumKey,
		enumDisplay: 'text',
		filter: { type: 'enum', enumKey, enumObject },
	}
}

export function iconEnumRenderer<TEnum extends EntityTableEnumLike>(
	key: string,
	enumObject: TEnum,
	enumKey: string,
): EntityTableColumnConfig {
	return {
		...enumRenderer(key, enumObject, enumKey),
		enumDisplay: 'iconWithTranslatedLabel',
	}
}

export function objectRenderer(
	key: string,
	objectKey: string,
): EntityTableColumnConfig {
	return {
		key,
		objectKey,
		renderer: 'object',
		filter: { type: 'builtIn', renderer: 'object' },
	}
}
